import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/features/auth/contexts/auth.context';
import { useUserContext } from '@/features/common/contexts/user.context';

/**
 * Syncs the authenticated user from better-auth session into UserContext.
 * Ensures user is available for features that depend on it (e.g. CV edit).
 * @returns isRetrievingAuth – true while the sync (login from session) is in progress
 */
export function useDashboardUserSync(): { isRetrievingAuth: boolean } {
  const { session, isLoading: isPending } = useAuth();
  const { user, login } = useUserContext();
  const isLoadingUserRef = useRef(false);
  const [isRetrievingAuth, setIsRetrievingAuth] = useState(false);

  useEffect(() => {
    if (isPending || !session?.user || user?.id || isLoadingUserRef.current) {
      return;
    }

    isLoadingUserRef.current = true;
    setIsRetrievingAuth(true);
    (async () => {
      try {
        await login();
      } catch (error) {
        console.error('Failed to sync user to UserContext', error);
      } finally {
        isLoadingUserRef.current = false;
        setIsRetrievingAuth(false);
      }
    })();
  }, [session?.user, isPending, user?.id, login]);

  return { isRetrievingAuth };
}
